import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Smartphone, Shield, ShieldAlert, ShieldCheck, ShieldX,
  Upload, Play, ChevronDown, ChevronUp, Terminal,
  Clock, Trash2, ArrowLeft, AlertTriangle, CheckCircle,
  XCircle, Lock, Wifi, Key, Code, Eye, Download, FileText,
  FileCode, FileJson, MapPin, Zap, Wrench, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  analyzeMobileApp,
  listMobileAnalyses,
  getMobileAnalysis,
  deleteMobileAnalysis,
  downloadMobileReport,
  getRiskColor,
  getSeverityColor,
} from '@/services/mobileAnalysis';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

const CATEGORY_ICONS = {
  ssl_pinning: Lock,
  data_storage: Key,
  runtime_protection: Shield,
  network_security: Wifi,
  crypto_analysis: ShieldAlert,
  code_injection: Code,
};

const FridaOutputBlock = ({ output, status }) => {
  const lines = (output || '').split('\n');
  return (
    <div className="bg-gray-950 rounded-lg overflow-hidden font-mono text-xs border border-gray-800">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-900 border-b border-gray-800">
        <Terminal className="w-3 h-3 text-green-400" />
        <span className="text-gray-400">frida-trace</span>
      </div>
      <div className="p-3 overflow-x-auto max-h-48 overflow-y-auto">
        {lines.map((line, i) => {
          let color = 'text-gray-400';
          if (line.startsWith('[+]') || line.includes('PASS')) color = 'text-emerald-400';
          else if (line.startsWith('[-]') || line.includes('FAIL')) color = 'text-red-400';
          else if (line.startsWith('[!]')) color = 'text-amber-400';
          else if (line.startsWith('[*]')) color = 'text-cyan-400';
          return <div key={i} className={`${color} leading-5`}>{line}</div>;
        })}
      </div>
    </div>
  );
};

const CodeBlock = ({ code, variant = 'default', label }) => {
  const styles = {
    default: 'bg-gray-950 text-gray-200 border-gray-800',
    bad: 'bg-red-950 text-red-100 border-red-900',
    good: 'bg-emerald-950 text-emerald-100 border-emerald-900',
  };
  if (!code) return null;
  return (
    <div className="rounded-md overflow-hidden border" data-testid={`code-block-${variant}`}>
      {label && (
        <div className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide ${
          variant === 'bad' ? 'bg-red-900 text-red-100' :
          variant === 'good' ? 'bg-emerald-900 text-emerald-100' :
          'bg-gray-900 text-gray-300'
        }`}>
          {label}
        </div>
      )}
      <pre className={`${styles[variant]} px-3 py-2 text-[11px] leading-[1.55] overflow-x-auto whitespace-pre font-mono max-h-72`}>
        {code}
      </pre>
    </div>
  );
};

const CheckRow = ({ check, isExpanded, onToggle }) => {
  const passed = check.status === 'pass';
  const inconclusive = check.status === 'inconclusive';
  const hasEnrichment = check.status === 'fail' && (check.vulnerable_code_pattern || check.remediation_code_after);
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className={`w-full px-4 py-3 flex items-center gap-3 text-left transition ${
          passed ? 'bg-emerald-50/50 hover:bg-emerald-50' : inconclusive ? 'bg-slate-50 hover:bg-slate-100' : 'bg-red-50/50 hover:bg-red-50'
        }`}
        onClick={onToggle}
        data-testid={`check-${check.id}`}
      >
        {passed ? (
          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        ) : inconclusive ? (
          <AlertTriangle className="w-4 h-4 text-slate-500 flex-shrink-0" />
        ) : (
          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{check.name}</span>
            <Badge className={`text-[10px] px-1.5 py-0 ${getSeverityColor(check.severity)}`}>
              {check.severity}
            </Badge>
            {inconclusive && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-600" data-testid={`inconclusive-badge-${check.id}`}>needs runtime test</Badge>
            )}
            {check.cwe && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{check.cwe}</Badge>
            )}
            {check.masvs && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">{check.masvs}</Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{check.description}</p>
        </div>
        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {isExpanded && (
        <div className="px-4 py-4 border-t bg-white space-y-4" data-testid={`check-detail-${check.id}`}>
          {check.frida_output ? (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                Frida Runtime Trace (simulated)
              </div>
              <FridaOutputBlock output={check.frida_output} status={check.status} />
            </div>
          ) : (
            <div data-testid={`evidence-${check.id}`}>
              <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                Evidence (from uploaded file)
              </div>
              <CodeBlock code={check.evidence_text || 'No matching evidence in the package.'} variant={check.status === 'fail' ? 'bad' : 'good'} />
            </div>
          )}

          {hasEnrichment && (
            <>
              {check.location && (
                <div data-testid={`finding-location-${check.id}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                    <MapPin className="w-3 h-3" /> Location
                  </div>
                  <div className="font-mono text-xs bg-slate-100 border border-dashed border-slate-300 rounded px-3 py-2 text-slate-700">
                    {check.location}
                  </div>
                </div>
              )}

              {check.vulnerable_code_pattern && (
                <div data-testid={`finding-vulncode-${check.id}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                    <Code className="w-3 h-3" /> {check.frida_output ? 'Vulnerable Code Pattern' : 'Matched Evidence'}
                  </div>
                  <CodeBlock code={check.vulnerable_code_pattern} variant="bad" />
                </div>
              )}

              {Array.isArray(check.exploitation_examples) && check.exploitation_examples.length > 0 && (
                <div data-testid={`finding-exploits-${check.id}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                    <Zap className="w-3 h-3" /> Exploitation Examples (Real-World Cases)
                  </div>
                  <div className="space-y-2">
                    {check.exploitation_examples.map((ex, i) => (
                      <div key={i} className="border-l-2 border-amber-500 bg-amber-50 rounded px-3 py-2">
                        <div className="text-xs font-bold text-amber-900">{ex.attack_name}</div>
                        {ex.real_world_case && (
                          <div className="text-xs text-gray-700 mt-1">
                            <span className="font-semibold">Real-world:</span> {ex.real_world_case}
                          </div>
                        )}
                        {ex.steps && (
                          <div className="text-xs text-gray-700 mt-1">
                            <span className="font-semibold">Steps:</span> {ex.steps}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(check.remediation_code_before || check.remediation_code_after) && (
                <div data-testid={`finding-remediation-${check.id}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1.5">
                    <Wrench className="w-3 h-3" /> Remediation (Code Replacement)
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CodeBlock code={check.remediation_code_before} variant="bad" label="Before" />
                    <CodeBlock code={check.remediation_code_after} variant="good" label="After" />
                  </div>
                </div>
              )}

              {Array.isArray(check.references) && check.references.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">
                    References
                  </div>
                  <ul className="text-xs text-indigo-700 space-y-0.5 list-disc pl-4">
                    {check.references.slice(0, 5).map((r, i) => (
                      <li key={i}>
                        <a href={r} target="_blank" rel="noreferrer" className="hover:underline break-all">
                          {r}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CategoryCard = ({ category }) => {
  const [expandedChecks, setExpandedChecks] = useState({});
  const Icon = CATEGORY_ICONS[category.category] || Shield;

  const toggleCheck = (checkId) => {
    setExpandedChecks(prev => ({ ...prev, [checkId]: !prev[checkId] }));
  };

  const statusColor = category.status === 'pass'
    ? 'border-l-emerald-500'
    : category.status === 'fail'
    ? 'border-l-red-500'
    : 'border-l-amber-500';

  return (
    <Card className={`border-l-4 ${statusColor}`} data-testid={`category-${category.category}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              category.status === 'pass' ? 'bg-emerald-100' : category.status === 'fail' ? 'bg-red-100' : 'bg-amber-100'
            }`}>
              <Icon className={`w-5 h-5 ${
                category.status === 'pass' ? 'text-emerald-600' : category.status === 'fail' ? 'text-red-600' : 'text-amber-600'
              }`} />
            </div>
            <div>
              <CardTitle className="text-base">{category.name}</CardTitle>
              <CardDescription className="text-xs">{category.description}</CardDescription>
            </div>
          </div>
          <div className="text-right">
            <span className="text-lg font-bold">{category.passed}/{category.total}</span>
            <p className="text-xs text-gray-500">passed</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {category.checks.map(check => (
          <CheckRow
            key={check.id}
            check={check}
            isExpanded={expandedChecks[check.id] || false}
            onToggle={() => toggleCheck(check.id)}
          />
        ))}
      </CardContent>
    </Card>
  );
};

const AnalysisDetail = ({ analysis, onBack, onRetest, retesting }) => {
  const [exporting, setExporting] = useState(false);
  if (!analysis?.results) return null;
  const { summary, categories } = analysis.results;

  const riskColor = getRiskColor(summary.risk_level);

  const handleExport = async (format) => {
    try {
      setExporting(true);
      await downloadMobileReport(analysis.id, format, analysis.app_name);
      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(`Failed to export ${format.toUpperCase()} report`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h2 className="text-xl font-bold">{analysis.app_name}</h2>
          <Badge variant="outline">{analysis.platform === 'android' ? 'Android' : 'iOS'}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRetest?.(analysis)}
            disabled={retesting}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="retest-btn"
          >
            {retesting ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mr-2" />
                Retesting...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" /> Retest
              </>
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={exporting}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                data-testid="export-report-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Report'}
                <ChevronDown className="w-3 h-3 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="export-menu">
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf">
                <FileText className="w-4 h-4 mr-2" /> Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('html')} data-testid="export-html">
                <FileCode className="w-4 h-4 mr-2" /> Export as HTML
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')} data-testid="export-json">
                <FileJson className="w-4 h-4 mr-2" /> Export as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {analysis.analysis_mode === 'simulated' ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="simulated-banner">
          <b>Simulated report.</b> This older analysis was generated from templates and was not derived from the uploaded file.
          Don't use it as audit evidence — upload the APK/IPA again for a real static analysis.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700" data-testid="static-analysis-meta">
          Static analysis of <span className="font-mono">{analysis.file_name}</span>
          {analysis.app_version && <> · version {analysis.app_version}</>}
          {analysis.file_sha256 && <> · SHA-256 <span className="font-mono">{analysis.file_sha256.slice(0, 16)}…</span></>}
          {summary.inconclusive > 0 && <> · {summary.inconclusive} check(s) inconclusive (need a runtime test)</>}
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={`border ${riskColor}`} data-testid="risk-score-card">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-black">{summary.risk_score}</p>
            <p className="text-xs font-semibold mt-1">{summary.risk_level} Risk</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-black text-gray-800">{summary.total_checks}</p>
            <p className="text-xs text-gray-500 mt-1">Total Checks</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-black text-emerald-600">{summary.passed}</p>
            <p className="text-xs text-gray-500 mt-1">Passed</p>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-black text-red-600">{summary.failed}</p>
            <p className="text-xs text-gray-500 mt-1">Failed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Progress value={(summary.passed / summary.total_checks) * 100} className="h-2 mt-2" />
            <p className="text-xs text-gray-500 mt-2">
              {Math.round((summary.passed / summary.total_checks) * 100)}% Compliance
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Package info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg text-sm">
        <div>
          <span className="text-gray-500 text-xs">Package ID</span>
          <p className="font-mono text-xs mt-0.5">{analysis.package_id}</p>
        </div>
        <div>
          <span className="text-gray-500 text-xs">Platform</span>
          <p className="capitalize mt-0.5">{analysis.platform}</p>
        </div>
        <div>
          <span className="text-gray-500 text-xs">Analyzed</span>
          <p className="mt-0.5">{new Date(analysis.created_at).toLocaleString()}</p>
        </div>
        {analysis.file_name && (
          <div>
            <span className="text-gray-500 text-xs">File</span>
            <p className="font-mono text-xs mt-0.5 truncate">{analysis.file_name}</p>
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {categories.map(cat => (
          <CategoryCard key={cat.category} category={cat} />
        ))}
      </div>
    </div>
  );
};

export default function MobileAnalysisPage() {
  const [analyses, setAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [retesting, setRetesting] = useState(false);
  const [appName, setAppName] = useState('');
  const [platform, setPlatform] = useState('android');
  const [packageId, setPackageId] = useState('');
  const [appFile, setAppFile] = useState(null);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      const data = await listMobileAnalyses();
      setAnalyses(data);
    } catch (err) {
      console.error('Failed to load analyses:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalyses(); }, []);

  const handleAnalyze = async () => {
    if (!appName.trim()) {
      toast.error('Please enter an application name');
      return;
    }
    if (!appFile) {
      toast.error('Upload the .apk or .ipa file to analyze');
      return;
    }
    try {
      setAnalyzing(true);
      const formData = new FormData();
      formData.append('app_name', appName);
      formData.append('platform', platform);
      if (packageId) formData.append('package_id', packageId);
      if (appFile) formData.append('app_file', appFile);

      const result = await analyzeMobileApp(formData);
      toast.success('Mobile analysis completed');
      setSelectedAnalysis(result);
      setAppName('');
      setPackageId('');
      setAppFile(null);
      fetchAnalyses();
    } catch (err) {
      console.error('Analysis failed:', err);
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteMobileAnalysis(id);
      toast.success('Analysis deleted');
      setAnalyses(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openDetail = async (analysis) => {
    try {
      const full = await getMobileAnalysis(analysis.id);
      setSelectedAnalysis(full);
    } catch (err) {
      toast.error('Failed to load analysis');
    }
  };

  const handleRetest = async (analysis) => {
    if (!analysis?.app_name) return;
    setAppName(analysis.app_name);
    setPlatform(analysis.platform || 'android');
    setSelectedAnalysis(null);
    toast.info('Upload the latest APK/IPA build to re-run the analysis');
  };

  if (selectedAnalysis) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <AnalysisDetail
          analysis={selectedAnalysis}
          onBack={() => setSelectedAnalysis(null)}
          onRetest={handleRetest}
          retesting={retesting}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="mobile-analysis-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Smartphone className="w-6 h-6 text-indigo-600" />
          Mobile Dynamic Analysis
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Static analysis of uploaded Android APKs and iOS IPAs — manifest/Info.plist config, embedded secrets, cleartext endpoints, exported components, crypto and protection indicators
        </p>
      </div>

      {/* New Analysis Form */}
      <Card data-testid="new-analysis-form">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Play className="w-4 h-4 text-indigo-600" />
            New Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="app-name">Application Name</Label>
              <Input
                id="app-name"
                placeholder="e.g., MyBankApp"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                data-testid="app-name-input"
              />
            </div>
            <div>
              <Label>Platform</Label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="android">Android (APK)</SelectItem>
                  <SelectItem value="ios">iOS (IPA)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="package-id">Package ID (optional)</Label>
              <Input
                id="package-id"
                placeholder="com.example.app"
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                data-testid="package-id-input"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleAnalyze}
                disabled={analyzing || !appName.trim() || !appFile}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="analyze-btn"
              >
                {analyzing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Analyze
                  </>
                )}
              </Button>
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="app-file" className="text-xs text-gray-500">
              Upload APK/IPA file (required — findings come only from this file)
            </Label>
            <Input
              id="app-file"
              type="file"
              accept=".apk,.ipa"
              onChange={(e) => setAppFile(e.target.files?.[0] || null)}
              className="mt-1"
              data-testid="app-file-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Previous Analyses */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            Previous Analyses
            {analyses.length > 0 && (
              <Badge variant="outline" className="ml-2">{analyses.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : analyses.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Smartphone className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No analyses yet. Run your first scan above.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {analyses.map(a => {
                const risk = a.results?.summary;
                const riskColor = risk ? getRiskColor(risk.risk_level) : '';
                return (
                  <div
                    key={a.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 cursor-pointer transition"
                    onClick={() => openDetail(a)}
                    data-testid={`analysis-row-${a.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-sm">
                          {a.app_name}
                          {a.analysis_mode === 'simulated' && (
                            <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700" data-testid={`simulated-tag-${a.id}`}>SIMULATED</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {a.platform} &middot; {a.package_id} &middot; {new Date(a.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {risk && (
                        <Badge className={`${riskColor} text-xs`}>
                          {risk.risk_level} ({risk.risk_score})
                        </Badge>
                      )}
                      {risk && (
                        <span className="text-xs text-gray-500">
                          {risk.passed}/{risk.total_checks} passed
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDelete(a.id, e)}
                        className="text-gray-400 hover:text-red-500"
                        data-testid={`delete-analysis-${a.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
